/**
 * Blog Articles Store - Alpine.js store for the blog article grid and tag filters
 * Shared between the main-blog section's grid and the separate blog-tag-filters block,
 * so both can be powered by the same fetch/filter state regardless of DOM position.
 */
import api from '@js/utils/api';

// Bumped from 2023-04 so article metafields and their metaobject/image references resolve
// (metaobjects are only exposed on the Storefront API from 2023-04 onwards).
const STOREFRONT_API_VERSION = '2025-10';

export default {
  name: 'blogArticles',
  store() {
    return {
      articles: [],
      loading: true,
      initialized: false,
      currentPage: 1,
      articlesPerPage: 12,
      paginationStyle: 'load_more',
      selectedTags: [],
      availableTags: [],
      resultCount: 0,
      resultCountLoading: false,
      resultCountEnabled: false,

      // Featured article (RAZ-307): one tagged article shown above the grid and
      // removed from the unfiltered ('All') listing so it never appears twice.
      featuredArticleEnabled: false,
      featuredArticleTag: '',
      featuredArticle: null,
      featuredArticleLoading: false,
      featuredArticleRequest: null,
      totalArticlesCount: 0,

      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        endCursor: null
      },
      cursors: [''],

      get totalPages() {
        return this.cursors.length + (this.pageInfo.hasNextPage ? 1 : 0);
      },

      // Whether any tag filter is currently applied (i.e. we are NOT on the "All" view)
      get hasActiveTagFilter() {
        return this.selectedTags.length > 0;
      },

      get displayedArticles() {
        if (this.featuredArticleEnabled && this.featuredArticle && !this.hasActiveTagFilter) {
          return this.articles.filter(article => article.id !== this.featuredArticle.id);
        }
        return this.articles;
      },

      // On the "All" view this is blog.articles_count as-is - the featured article is still
      // one of the blog's articles, so it stays in the total even though it's pulled out of
      // the grid. Only fall back to counting loaded/filtered articles once a tag is active.
      get resultsCount() {
        if (!this.hasActiveTagFilter && this.totalArticlesCount) {
          return this.totalArticlesCount;
        }
        return this.displayedArticles.length;
      },

      // Only shown on the "All" view - hidden as soon as a tag filter is active
      get showFeaturedArticle() {
        return this.featuredArticleEnabled && !this.hasActiveTagFilter && !!this.featuredArticle;
      },

      init() {},

      configure(config = {}) {
        if (this.initialized) return;
        this.initialized = true;

        if (config.articlesPerPage) {
          this.articlesPerPage = config.articlesPerPage;
        }

        this.featuredArticleEnabled = !!config.featuredArticleEnabled;
        this.featuredArticleTag = config.featuredArticleTag || '';

        if (this.featuredArticleEnabled && this.featuredArticleTag) {
          this.featuredArticleRequest = this.fetchFeaturedArticle();
        }

        this.fetchArticles();
      },

      // Called by the blog-tag-filters block on mount, wherever it sits on the page
      registerFilters(tagConfiguration) {
        this.parseTagConfiguration(tagConfiguration);
      },

      // Called by the blog-tag-filters block on mount to seed the exact "All view"
      // total from Liquid's blog.articles_count. Independent of configure() so the filters
      // block doesn't need to depend on main-blog.liquid's init order.
      registerResultsCount(totalCount) {
        this.totalArticlesCount = totalCount || 0;
      },

      parseTagConfiguration(tagConfig) {
        if (!tagConfig) return;

        // Handle different line ending types
        const lines = tagConfig.split(/\r?\n/).filter(line => line.trim());
        this.availableTags = lines.map(line => {
          const [tagValue, tagLabel] = line.split('=').map(part => part.trim());
          return {
            value: tagValue,
            label: tagLabel || tagValue
          };
        });
      },

      toggleTag(tagValue) {
        if (this.selectedTags.includes(tagValue)) {
          this.selectedTags = this.selectedTags.filter(tag => tag !== tagValue);
        } else {
          this.selectedTags.push(tagValue);
        }
        this.resetAndFetchArticles();
      },

      clearAllFilters() {
        this.selectedTags = [];
        this.resetAndFetchArticles();
      },

      resetAndFetchArticles() {
        this.currentPage = 1;
        this.articles = [];
        this.cursors = [''];
        this.pageInfo = {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null,
          endCursor: null
        };
        this.fetchArticles();
        this.fetchResultCount();
      },

      buildTagQuery() {
        if (this.selectedTags.length === 0) return null;
        return this.selectedTags.map(tag => `tag:"${tag}"`).join(' OR ');
      },

      // Storefront search syntax splits an unquoted value on whitespace, so a multi-word
      // tag ('Fitting Guides') only matches when it is quoted.
      quoteTag(tag) {
        return `'${String(tag).replace(/'/g, '')}'`;
      },

      // Identifiers configured in Theme Settings > Article Metafields Data, exposed by
      // head-scripts.liquid. A 'namespace.key.field' line still fetches 'namespace.key' —
      // the sub-field is resolved client-side from the expanded metaobject reference.
      getMetafieldIdentifiers() {
        const configured = Array.isArray(window.articleMetafieldIdentifiers) ? window.articleMetafieldIdentifiers : [];
        const seen = new Set();

        return configured.reduce((identifiers, entry) => {
          if (!entry?.namespace || !entry?.key) return identifiers;
          const id = `${entry.namespace}.${entry.key}`;
          if (seen.has(id)) return identifiers;
          seen.add(id);
          identifiers.push({ namespace: entry.namespace, key: entry.key });
          return identifiers;
        }, []);
      },

      // Article node fields shared by the listing and the featured article query
      articleFields(hasMetafields) {
        return `
          id
          title
          handle
          content
          excerpt
          tags
          publishedAt
          author: authorV2 {
            name
          }
          blog {
            title
            handle
          }
          image {
            url
            altText
            width
            height
          }
          seo {
            title
            description
          }
          ${hasMetafields ? `
          metafields(identifiers: $metafieldIdentifiers) {
            namespace
            key
            value
            type
            reference {
              ... on MediaImage {
                image { url altText width height }
              }
              ... on Metaobject {
                type
                handle
                fields {
                  key
                  value
                  reference {
                    ... on MediaImage {
                      image { url altText width height }
                    }
                  }
                }
              }
            }
          }` : ''}
        `;
      },

      async graphqlRequest(query, variables) {
        const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
          },
          body: JSON.stringify({ query, variables })
        });

        const payload = await response.json();

        if (payload?.errors?.length) {
          console.error('[arctheme] Storefront API errors:', payload.errors);
          return null;
        }

        return payload?.data || null;
      },

      // Runs an article query with the configured metafield identifiers, retrying without
      // them if they make it fail — a misconfigured metafield must never empty the listing
      // or drop the featured article.
      async queryWithMetafields(runQuery) {
        const identifiers = this.getMetafieldIdentifiers();
        const data = await runQuery(identifiers);
        if (data || identifiers.length === 0) return data;

        console.warn('[arctheme] Retrying article query without metafields');
        return runQuery([]);
      },

      // Runs a query built from the metafield-aware variable declaration, passing the
      // identifiers through as $metafieldIdentifiers when any are configured
      runQueryWithIdentifiers(buildQuery, variables, identifiers) {
        const hasMetafields = identifiers.length > 0;
        const metafieldsVariable = hasMetafields ? ', $metafieldIdentifiers: [HasMetafieldsIdentifier!]!' : '';

        return this.graphqlRequest(buildQuery(metafieldsVariable, this.articleFields(hasMetafields)), {
          ...variables,
          ...(hasMetafields && { metafieldIdentifiers: identifiers })
        });
      },

      queryArticles(variables) {
        const buildQuery = (metafieldsVariable, fields) => `
          query getArticles($first: Int!, $after: String, $query: String${metafieldsVariable}) {
            articles(first: $first, after: $after, query: $query) {
              edges {
                cursor
                node {${fields}}
              }
              pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
              }
            }
          }
        `;

        return this.queryWithMetafields(identifiers => this.runQueryWithIdentifiers(buildQuery, variables, identifiers));
      },

      queryFeaturedArticle(variables) {
        const buildQuery = (metafieldsVariable, fields) => `
          query getFeaturedArticle($query: String!${metafieldsVariable}) {
            articles(first: 1, query: $query) {
              edges {
                node {${fields}}
              }
            }
          }
        `;

        return this.queryWithMetafields(identifiers => this.runQueryWithIdentifiers(buildQuery, variables, identifiers));
      },

      async fetchArticles(cursor = null) {
        try {
          this.loading = true;

          // On the "All" view the featured article sits above the grid, so it must not use up
          // one of the page's slots - fetch one extra and drop it from the listing instead
          if (this.featuredArticleRequest && !this.hasActiveTagFilter) {
            await this.featuredArticleRequest;
          }
          const excludedArticleId = this.showFeaturedArticle ? this.featuredArticle.id : null;

          const data = await this.queryArticles({
            first: this.articlesPerPage + (excludedArticleId ? 1 : 0),
            after: cursor,
            query: this.buildTagQuery()
          });

          if (data?.articles) {
            const listedEdges = data.articles.edges.filter(edge => edge.node.id !== excludedArticleId);
            const pageEdges = listedEdges.slice(0, this.articlesPerPage);

            // When the extra article was trimmed, continue the next page from the last one shown
            // so it isn't skipped
            this.pageInfo = listedEdges.length > pageEdges.length
              ? { ...data.articles.pageInfo, hasNextPage: true, endCursor: pageEdges[pageEdges.length - 1].cursor }
              : data.articles.pageInfo;

            // Transform GraphQL articles for Alpine usage
            const graphqlArticles = pageEdges.map(edge => edge.node);

            if (this.paginationStyle === 'load_more' && cursor) {
              // For load more, append to existing articles
              this.articles = [...this.articles, ...this.transformGraphqlArticles(graphqlArticles)];
            } else {
              // For pagination, replace articles
              this.articles = this.transformGraphqlArticles(graphqlArticles);
            }

            // Update cursor tracking for pagination
            if (this.pageInfo.hasNextPage && !this.cursors.includes(this.pageInfo.endCursor)) {
              this.cursors[this.currentPage] = this.pageInfo.endCursor;
            }
          }

          this.loading = false;
        } catch (error) {
          console.error('Error fetching articles:', error);
          this.loading = false;
        }
      },

      // Fetches the single featured article (first match) for the configured tag.
      // Runs once, independently of the main listing fetch/pagination.
      async fetchFeaturedArticle() {
        if (!this.featuredArticleTag) return;

        try {
          this.featuredArticleLoading = true;

          const data = await this.queryFeaturedArticle({
            query: `tag:${this.quoteTag(this.featuredArticleTag)}`
          });
          const node = data?.articles?.edges?.[0]?.node || null;

          // Only the first match is ever used, even if multiple articles share the tag
          this.featuredArticle = node ? this.transformGraphqlArticles([node])[0] : null;

          this.featuredArticleLoading = false;
        } catch (error) {
          console.error('Error fetching featured article:', error);
          this.featuredArticle = null;
          this.featuredArticleLoading = false;
        }
      },

      async goToNextPage() {
        if (this.pageInfo.hasNextPage) {
          this.loading = true;
          this.currentPage++;
          await this.fetchArticles(this.pageInfo.endCursor);
        }
      },

      async goToPreviousPage() {
        if (this.currentPage > 1) {
          this.loading = true;
          this.currentPage--;
          // For previous page, we need to fetch from the cursor of the previous page
          const previousCursor = this.currentPage === 1 ? null : this.cursors[this.currentPage - 1];
          await this.fetchArticles(previousCursor);
        }
      },

      async loadMore() {
        if (this.pageInfo.hasNextPage) {
          await this.fetchArticles(this.pageInfo.endCursor);
        }
      },

      transformGraphqlArticles(graphqlArticles) {
        return graphqlArticles.map(article => ({
          id: article.id,
          title: article.title,
          handle: article.handle,
          content: article.content,
          excerpt: article.excerpt,
          tags: article.tags || [],
          published_at: article.publishedAt,
          created_at: article.publishedAt,
          author: article.author?.name || 'Unknown Author',
          blog: {
            title: article.blog?.title || '',
            handle: article.blog?.handle || ''
          },
          image: article.image ? {
            src: article.image.url,
            alt: article.image.altText || article.title,
            width: article.image.width,
            height: article.image.height
          } : null,
          seo: article.seo || {},
          metafields: this.transformMetafields(article.metafields),
          url: `/blogs/${article.blog?.handle || 'news'}/${article.handle}`,
          // Add computed properties
          reading_time: this.calculateReadingTime(article.content),
          formatted_date: this.formatDate(article.publishedAt)
        }));
      },

      // Flattened { namespace, key, value, type, reference } entries, matching the shape the
      // product tile uses — article tile blocks read them through the $articleMetafield magic.
      transformMetafields(metafields) {
        if (!Array.isArray(metafields)) return [];

        return metafields
          .filter(Boolean)
          .map(({ namespace, key, value, type, reference }) => ({
            namespace,
            key,
            value,
            type,
            reference: reference || null
          }));
      },

      calculateReadingTime(content) {
        if (!content) return 0;
        const wordsPerMinute = 200;
        const words = content.split(/\s+/).length;
        return Math.ceil(words / wordsPerMinute);
      },

      formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    };
  }
};